import {StyleSheet} from "aphrodite";
import React from "react";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import type {PropsFor, StyleType} from "@khanacademy/wonder-blocks-core";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import {FavoriteButton} from "./favorite-button.tsx";
import type {Platform, Tool, ToolPersona} from "./types.ts";
import {i18nToolCategory} from "./utils/categories.ts";

type DetailCellProps = PropsFor<typeof DetailCell>;

interface Props {
    tool: Tool;
    persona: ToolPersona;
    style?: StyleType;
    horizontalRule?: DetailCellProps["horizontalRule"];
    disabled?: boolean;
    onClick?: (tool: Tool) => void;
    platform?: Platform;
    favorited?: boolean;
    updateToolFavorited?: (configName: string, isFavorited: boolean) => void;
    showFavoriteButton?: boolean;
    classCode?: string;
    assignmentContentId?: string;
}

export const ToolCard = ({
    tool,
    persona,
    style,
    horizontalRule,
    disabled,
    onClick,
    platform,
    favorited,
    updateToolFavorited,
    showFavoriteButton = false,
    classCode,
    assignmentContentId,
}: Props): React.ReactElement => {
    const isWritingCoach = tool.configName === "writing-coach-getting-started";
    const khanmigoToolPath =
        // default to teacher because there are more usages that assume the card will point to the teacher version.
        persona === "PARENT" ? "/parent/tools" : "/teacher/tools";
    let href = `${khanmigoToolPath}/${tool.urlSlug}`;
    if (platform) {
        href += `?platform=${platform}`;
    }
    if (classCode && assignmentContentId) {
        href = `/teacher/class/${classCode}/edit-your-assignment/${assignmentContentId}`;
        if (isWritingCoach) {
            href = "/writing-coach/assignment/create";
        }
    }

    return (
        <DetailCell
            href={href}
            target={isWritingCoach ? "_blank" : undefined}
            title={<LabelLarge>{tool.title()}</LabelLarge>}
            subtitle1={
                <LabelSmall style={styles.category}>
                    {i18nToolCategory(tool.categories[0])}
                </LabelSmall>
            }
            subtitle2={
                <LabelSmall style={styles.description}>
                    {tool.description()}
                </LabelSmall>
            }
            contentStyle={{alignSelf: "flex-start"}}
            rightAccessory={
                showFavoriteButton && (
                    <FavoriteButton
                        isFavorited={favorited ?? false}
                        configName={tool.configName}
                        updateToolFavorited={updateToolFavorited}
                    />
                )
            }
            rightAccessoryStyle={{alignSelf: "flex-start"}}
            leftAccessoryStyle={{alignSelf: "flex-start"}}
            style={style}
            rootStyle={styles.rootStyle}
            horizontalRule={horizontalRule ?? "none"}
            disabled={disabled}
            data-testid="ai-tool-card"
            onClick={() => onClick?.(tool)}
            role="button"
            testId="ai-tool-card"
        />
    );
};

const styles = StyleSheet.create({
    category: {
        marginBlockEnd: sizing.size_080,
        color: semanticColor.core.foreground.neutral.default,
    },
    description: {
        marginBlockStart: sizing.size_020,
        color: semanticColor.core.foreground.neutral.default,
    },
    rootStyle: {
        borderRadius: border.radius.radius_080,
    },
});
